import { RefObject, useEffect, useRef, useState } from 'react'

type UseParentOverflowDetectionProps = {
  childRef: RefObject<HTMLElement>
  parentRef: RefObject<HTMLElement>
  threshold?: number
}

type OverflowDetectionResult = {
  isOverflowing: boolean
  overflowWidth: number | null
}

function useParentOverflowDetection({
  childRef,
  parentRef,
  threshold = 0
}: UseParentOverflowDetectionProps): OverflowDetectionResult {
  const [overflowState, setOverflowState] = useState<OverflowDetectionResult>({
    isOverflowing: false,
    overflowWidth: null
  })

  const overflowThresholdRef = useRef<number | null>(null)

  useEffect(() => {
    const checkOverflow = () => {
      if (!childRef.current || !parentRef.current) {
        setOverflowState({ isOverflowing: false, overflowWidth: null })
        overflowThresholdRef.current = null
        return
      }

      const childWidth = childRef.current.scrollWidth
      const parentWidth = parentRef.current.clientWidth

      if (overflowThresholdRef.current !== null) {
        const isOverflowingParent = parentWidth < overflowThresholdRef.current

        setOverflowState({
          isOverflowing: isOverflowingParent,
          overflowWidth: overflowThresholdRef.current
        })
        return
      }

      const isOverflowing = childWidth + threshold > parentWidth

      if (isOverflowing) {
        overflowThresholdRef.current = childWidth + threshold
        setOverflowState({
          isOverflowing: true,
          overflowWidth: childWidth + threshold
        })
      } else {
        setOverflowState({
          isOverflowing: false,
          overflowWidth: null
        })
      }
    }

    checkOverflow()

    const resizeObserver = new ResizeObserver(checkOverflow)
    const child = childRef.current
    const parent = parentRef.current

    if (child) resizeObserver.observe(child)
    if (parent) resizeObserver.observe(parent)

    return () => resizeObserver.disconnect()
  }, [childRef, parentRef, threshold])

  return overflowState
}

export default useParentOverflowDetection
